// Construction sites on pavement lanes: orange striped barriers, cones and a
// warning sign (tallest element ~1.2 — clearable with a good jump). Fairness
// rules verbatim from v1 obstacle-manager.js:105-126: never block both
// pavements in the same stretch, never stack sites in one lane.

import * as THREE from 'three';
import { LANES, SPAWN_DISTANCE } from '@/config/tuning';
import type { Rng } from '@/core/rng';

const SITE_WIDTH = 2.6;
const SITE_DEPTH = 8; // v1 fixed 8-unit depth
const ESCAPE_MARGIN = 16;
const SAME_LANE_GAP = 6;
// v1 spawns construction 40 units beyond the normal spawn distance
const CONSTRUCTION_SPAWN_Z = SPAWN_DISTANCE - 40;

export interface ConstructionSite {
  group: THREE.Group;
  width: number;
  depth: number;
  clearBonusAwarded: boolean;
}

const barrierMat = new THREE.MeshLambertMaterial({ color: 0xff6600 });
const stripeMat = new THREE.MeshLambertMaterial({ color: 0xffffff });
const coneMat = new THREE.MeshLambertMaterial({ color: 0xff6600 });
const signMat = new THREE.MeshLambertMaterial({ color: 0xffff00 });
const postMat = new THREE.MeshLambertMaterial({ color: 0x666666 });

// Shared geometries — sites reuse one buffer per part (Phase 7)
const barrierGeo = new THREE.BoxGeometry(SITE_WIDTH, 0.5, 0.15);
const stripeGeo = new THREE.BoxGeometry(SITE_WIDTH + 0.02, 0.12, 0.16);
const legGeo = new THREE.BoxGeometry(0.08, 0.45, 0.08);
const coneGeo = new THREE.ConeGeometry(0.14, 0.4, 8);
const signPostGeo = new THREE.CylinderGeometry(0.04, 0.04, 1.0, 6);
const signGeo = new THREE.BoxGeometry(0.55, 0.55, 0.04);

function buildSite(rng: Rng): THREE.Group {
  const group = new THREE.Group();

  // Barriers across both ends and the middle
  for (const z of [-SITE_DEPTH / 2 + 0.3, 0, SITE_DEPTH / 2 - 0.3]) {
    const barrier = new THREE.Mesh(barrierGeo, barrierMat);
    barrier.position.set(0, 0.45, z);
    barrier.castShadow = true;
    group.add(barrier);
    const stripe = new THREE.Mesh(stripeGeo, stripeMat);
    stripe.position.set(0, 0.52, z);
    group.add(stripe);
    for (const x of [-SITE_WIDTH / 2 + 0.1, SITE_WIDTH / 2 - 0.1]) {
      const leg = new THREE.Mesh(legGeo, postMat);
      leg.position.set(x, 0.22, z);
      group.add(leg);
    }
  }

  // Cones scattered inside
  for (let i = 0; i < 4; i++) {
    const cone = new THREE.Mesh(coneGeo, coneMat);
    cone.position.set(
      (rng.next() - 0.5) * (SITE_WIDTH - 0.6),
      0.28,
      (rng.next() - 0.5) * (SITE_DEPTH - 2),
    );
    group.add(cone);
  }

  // Warning sign — the ~1.2-unit-tall element that sets the jump height bar
  const post = new THREE.Mesh(signPostGeo, postMat);
  post.position.set(0, 0.58, -1);
  group.add(post);
  const sign = new THREE.Mesh(signGeo, signMat);
  sign.position.set(0, 1.0, -1);
  sign.rotation.z = Math.PI / 4;
  group.add(sign);

  return group;
}

export class ConstructionSystem {
  readonly sites: ConstructionSite[] = [];

  constructor(
    private readonly scene: THREE.Scene,
    private readonly rng: Rng,
  ) {}

  /** v1 fairness guard (obstacle-manager.js:105-126). */
  private isUnfairPlacement(lane: 0 | 3, z: number): boolean {
    const laneX = LANES[lane];
    const otherX = LANES[lane === 0 ? 3 : 0];
    for (const site of this.sites) {
      const edgeGap = Math.abs(site.group.position.z - z) - (site.depth + SITE_DEPTH) / 2;
      if (Math.abs(site.group.position.x - otherX) < 0.1 && edgeGap < ESCAPE_MARGIN) return true;
      if (Math.abs(site.group.position.x - laneX) < 0.1 && edgeGap < SAME_LANE_GAP) return true;
    }
    return false;
  }

  /** True if an enemy spawn position would land inside a site. */
  isPositionBlocked(x: number, z: number): boolean {
    for (const site of this.sites) {
      if (
        Math.abs(site.group.position.x - x) < site.width / 2 + 0.5 &&
        Math.abs(site.group.position.z - z) < site.depth / 2 + 1
      ) {
        return true;
      }
    }
    return false;
  }

  spawn(): void {
    let lane: 0 | 3 = this.rng.next() < 0.5 ? 0 : 3;
    if (this.isUnfairPlacement(lane, CONSTRUCTION_SPAWN_Z)) {
      const other: 0 | 3 = lane === 0 ? 3 : 0;
      if (this.isUnfairPlacement(other, CONSTRUCTION_SPAWN_Z)) return; // Skip silently (v1)
      lane = other;
    }
    const group = buildSite(this.rng);
    group.position.set(LANES[lane], 0, CONSTRUCTION_SPAWN_Z);
    this.scene.add(group);
    this.sites.push({ group, width: SITE_WIDTH, depth: SITE_DEPTH, clearBonusAwarded: false });
  }

  update(dt: number, gameSpeed: number): void {
    const dz = gameSpeed * dt * 60;
    for (let i = this.sites.length - 1; i >= 0; i--) {
      const site = this.sites[i];
      if (!site) continue;
      site.group.position.z += dz;
      if (site.group.position.z > 20) this.despawn(i);
    }
  }

  despawn(index: number): void {
    const site = this.sites[index];
    if (!site) return;
    this.scene.remove(site.group);
    this.sites.splice(index, 1);
  }

  clear(): void {
    for (let i = this.sites.length - 1; i >= 0; i--) this.despawn(i);
  }
}
